import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Globe, ArrowRight, Check, Zap, TrendingUp, 
  Play, Users, Building2, ChevronRight, Sparkles,
  Send, CheckCircle, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CampaignLanding() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    marketing_consent: false
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [playingVideo, setPlayingVideo] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name || !formData.email || !formData.company) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads`, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        message: formData.message,
        marketing_consent: formData.marketing_consent,
        source: 'go-global'
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-black text-white mb-4">You're In!</h1>
          <p className="text-zinc-400 mb-8">
            Thank you for your interest. We'll be in touch within 24 hours to discuss how BrandStage 
            can help <span className="text-white font-semibold">{formData.company}</span> expand globally.
          </p>
          <div className="space-y-3">
            <Link 
              to="/profile/create" 
              className="block w-full bg-[#C41E3A] hover:bg-[#A01830] text-white py-4 px-6 rounded-lg font-bold transition-colors"
              data-testid="create-profile-cta"
            >
              Create Your Profile Now (Free)
            </Link>
            <Link 
              to="/"
              className="block w-full border border-zinc-700 hover:border-zinc-500 text-white py-4 px-6 rounded-lg font-medium transition-colors"
            >
              Explore BrandStage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="campaign-landing">
      {/* Floating Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png" 
              alt="BrandStage" 
              className="h-8"
            />
          </Link>
          <a 
            href="#get-started" 
            className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-colors"
          >
            Get Started
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#C41E3A]/10 via-transparent to-blue-500/5" />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-[#C41E3A]/5 rounded-full blur-3xl" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-zinc-800/50 border border-zinc-700 px-4 py-2 rounded-full text-sm mb-6">
                <Globe className="w-4 h-4 text-[#C41E3A]" />
                <span className="text-zinc-300">Leadership Visibility Platform</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-6">
                Your Leader<br />
                <span className="text-[#C41E3A]">Is Visible.</span>
              </h1>
              
              <p className="text-lg md:text-xl text-zinc-300 leading-relaxed mb-8">
                In today's digital world, <span className="text-white font-semibold">video content builds trust faster</span>. 
                Your leadership interviews become discoverable by prospects, 
                partners, and investors worldwide. A BrandStage profile puts your <span className="text-white font-semibold">business leader front and center</span>.
              </p>

              {/* Key Points */}
              <div className="space-y-3 mb-8">
                {[
                  'Leadership visibility that prospects discover first',
                  'Video-first storytelling that builds instant trust',
                  'Profile content translated into 13 languages automatically',
                  'One link for WhatsApp, LinkedIn, Email, everywhere'
                ].map((point, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#C41E3A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#C41E3A]" />
                    </div>
                    <span className="text-zinc-300">{point}</span>
                  </div>
                ))}
              </div>

              {/* Language flags inline */}
              <div className="flex items-center gap-2 mb-8 flex-wrap">
                <span className="text-zinc-500 text-sm">Profile text in:</span>
                <div className="flex items-center gap-1">
                  {['🇬🇧', '🇻🇳', '🇹🇭', '🇨🇳', '🇯🇵', '🇮🇳', '🇦🇪', '🇮🇩', '🇲🇾', '🇰🇷', '🇩🇪', '🇫🇷', '🇪🇸'].map((flag, i) => (
                    <span key={i} className="text-sm">{flag}</span>
                  ))}
                </div>
              </div>

              <a 
                href="#get-started" 
                className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-8 py-4 rounded-lg font-bold transition-colors text-lg"
              >
                Make Your Leader Visible
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>

            {/* Right - Visual */}
            <div className="relative hidden lg:block">
              <div className="relative">
                {/* Glowing profile card mockup */}
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700 rounded-2xl p-6 shadow-2xl">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#C41E3A] to-[#8B1428] rounded-xl flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">Your Company</h3>
                      <p className="text-zinc-400 text-sm">Going Global Since 2024</p>
                    </div>
                  </div>
                  
                  {/* Video placeholder with OG image */}
                  <div className="aspect-video rounded-lg mb-4 relative overflow-hidden">
                    <img 
                      src="https://static.prod-images.emergentagent.com/jobs/3b3b930a-8c3c-49e6-8785-22b39d87d257/images/79a289bb58fdd30bd15d2212b9679c188fff3b305e28a4cf8ffac29a8b2a30c0.jpeg"
                      alt="BrandStage - Video-First Profiles for Business Leaders"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30 hover:bg-white/20 transition-all cursor-pointer hover:scale-110">
                        <Play className="w-6 h-6 text-white ml-1" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">25+</p>
                      <p className="text-zinc-500 text-xs">Countries</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-[#C41E3A]">87</p>
                      <p className="text-zinc-500 text-xs">Digital Score</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">13</p>
                      <p className="text-zinc-500 text-xs">Languages</p>
                    </div>
                  </div>
                  
                  {/* Live indicator */}
                  <div className="flex items-center justify-center gap-2 text-sm text-green-400">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    Profile is Live
                  </div>
                </div>
                
                {/* Floating badge */}
                <div className="absolute -top-4 -right-4 bg-green-500 text-white px-4 py-2 rounded-lg font-bold text-sm shadow-lg">
                  <Sparkles className="w-4 h-4 inline mr-1" />
                  Video-First
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Strip */}
      <section className="py-16 bg-zinc-900 border-y border-zinc-800">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Without BrandStage */}
            <div className="bg-zinc-800/30 border border-red-500/20 rounded-xl p-8">
              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Without BrandStage</h3>
              <ul className="space-y-2 text-zinc-400">
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">×</span>
                  Static PDFs that AI cannot index or discover
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">×</span>
                  No video presence = no trust signal
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">×</span>
                  Leadership hidden behind corporate walls
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">×</span>
                  Lost opportunities when prospects search
                </li>
              </ul>
            </div>

            {/* With BrandStage */}
            <div className="bg-zinc-800/30 border border-green-500/20 rounded-xl p-8">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">With BrandStage</h3>
              <ul className="space-y-2 text-zinc-400">
                <li className="flex items-start gap-2">
                  <Check className="text-green-400 w-4 h-4 mt-1 flex-shrink-0" />
                  Your story told in 13 languages automatically
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-green-400 w-4 h-4 mt-1 flex-shrink-0" />
                  Leadership interviews that build instant credibility
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-green-400 w-4 h-4 mt-1 flex-shrink-0" />
                  Your profile pulls connections to all your digital assets
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-green-400 w-4 h-4 mt-1 flex-shrink-0" />
                  Update your profile to suit your needs instantly
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-green-400 w-4 h-4 mt-1 flex-shrink-0" />
                  Video content that LLMs can search and recommend
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-green-400 w-4 h-4 mt-1 flex-shrink-0" />
                  Create showreels of trade show participation instantly
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-green-400 w-4 h-4 mt-1 flex-shrink-0" />
                  Link podcasts, speeches, product launches to your profile
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-green-400 w-4 h-4 mt-1 flex-shrink-0" />
                  Make your profile a living asset that engages and influences
                </li>
              </ul>
              <p className="text-green-400 font-semibold text-sm mt-4 pt-4 border-t border-green-500/20">
                → First impression before any meeting happens
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Profile Explanation */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 px-4 py-2 rounded-full text-sm mb-6">
            <Sparkles className="w-4 h-4 text-blue-400" />
            <span className="text-blue-400">Dynamic Profile</span>
          </div>
          
          <h2 className="text-2xl md:text-3xl font-black text-white mb-6">
            Your Profile, Always Current
          </h2>
          
          <p className="text-lg text-zinc-300 leading-relaxed max-w-2xl mx-auto">
            Enter a few keywords about your business and expertise. BrandStage's AI turns them into a profile 
            you can review, personalise and update as your business evolves.
          </p>
          
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-sm">
            <div className="flex items-center gap-2 text-zinc-400">
              <Check className="w-4 h-4 text-green-400" />
              <span>Start with keywords</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-400">
              <ChevronRight className="w-4 h-4 text-zinc-600" />
            </div>
            <div className="flex items-center gap-2 text-zinc-400">
              <Check className="w-4 h-4 text-green-400" />
              <span>AI generates your story</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-400">
              <ChevronRight className="w-4 h-4 text-zinc-600" />
            </div>
            <div className="flex items-center gap-2 text-zinc-400">
              <Check className="w-4 h-4 text-green-400" />
              <span>You refine & publish</span>
            </div>
          </div>
        </div>
      </section>

      {/* Video-First Philosophy + Interview Examples */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#C41E3A]/20 px-4 py-2 rounded-full text-sm mb-6">
              <Play className="w-4 h-4 text-[#C41E3A]" />
              <span className="text-[#C41E3A]">Video-First Philosophy</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              Why Video-First Wins<br />
              <span className="text-zinc-400">For Growing Businesses</span>
            </h2>
            
            <p className="text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              AI and LLMs now analyze video content—transcribing, indexing, and surfacing it in search results. 
              Your leadership interview isn't just content; it's <span className="text-white">discoverable intelligence</span> that 
              positions your business when prospects are actively searching.
            </p>
          </div>

          {/* Why Video Works */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
              <div className="text-4xl font-black text-[#C41E3A] mb-2">85%</div>
              <p className="text-zinc-400 text-sm">of buyers prefer video over text when learning about a business</p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
              <div className="text-4xl font-black text-white mb-2">2x</div>
              <p className="text-zinc-400 text-sm">more likely to be remembered after watching a leadership video</p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
              <div className="text-4xl font-black text-blue-400 mb-2">48hr</div>
              <p className="text-zinc-400 text-sm">turnaround from interview to published profile</p>
            </div>
          </div>

          {/* Interview Examples */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-white text-center mb-8">See Leadership Interviews in Action</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Interview 1 */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                <div className="aspect-video relative">
                  {playingVideo === 'interview1' ? (
                    <iframe
                      src="https://www.youtube.com/embed/23M63uS7DOM?autoplay=1"
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="Leadership Interview Sample 1"
                    />
                  ) : (
                    <div 
                      className="w-full h-full bg-zinc-800 flex items-center justify-center cursor-pointer group relative"
                      onClick={() => setPlayingVideo('interview1')}
                    >
                      <img 
                        src="https://img.youtube.com/vi/23M63uS7DOM/maxresdefault.jpg"
                        alt="Leadership Interview"
                        className="absolute inset-0 w-full h-full object-cover opacity-60"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                      <div className="w-16 h-16 bg-[#C41E3A] rounded-full flex items-center justify-center z-10 group-hover:scale-110 transition-transform shadow-lg">
                        <Play className="w-7 h-7 text-white ml-1" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h4 className="text-white font-semibold">Leadership Interview Sample</h4>
                  <p className="text-zinc-500 text-sm">2-3 minute professional introduction</p>
                </div>
              </div>

              {/* Interview 2 */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                <div className="aspect-video relative">
                  {playingVideo === 'interview2' ? (
                    <iframe
                      src="https://www.youtube.com/embed/uUgtxhIcw4M?autoplay=1"
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="Leadership Interview Sample 2"
                    />
                  ) : (
                    <div 
                      className="w-full h-full bg-zinc-800 flex items-center justify-center cursor-pointer group relative"
                      onClick={() => setPlayingVideo('interview2')}
                    >
                      <img 
                        src="https://img.youtube.com/vi/uUgtxhIcw4M/maxresdefault.jpg"
                        alt="Quick Bites Interview"
                        className="absolute inset-0 w-full h-full object-cover opacity-60"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                      <div className="w-16 h-16 bg-[#C41E3A] rounded-full flex items-center justify-center z-10 group-hover:scale-110 transition-transform shadow-lg">
                        <Play className="w-7 h-7 text-white ml-1" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h4 className="text-white font-semibold">Quick Bites Format</h4>
                  <p className="text-zinc-500 text-sm">30-60 second impactful introduction</p>
                </div>
              </div>
            </div>
          </div>

          {/* Multi-language note - moved to hero context */}
        </div>
      </section>

      {/* Lead Capture Form */}
      <section id="get-started" className="py-20 px-6 bg-gradient-to-b from-zinc-900 to-zinc-950">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#C41E3A]/20 px-4 py-2 rounded-full text-sm mb-6">
              <Users className="w-4 h-4 text-[#C41E3A]" />
              <span className="text-[#C41E3A]">Leadership Visibility</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              Make Your Leader Visible
            </h2>
            <p className="text-zinc-400">
              Get in touch and discover how BrandStage can put your business leadership in front of the right audience.
            </p>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8"
            data-testid="lead-capture-form"
          >
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-6 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-5">
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Your Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="John Smith"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-[#C41E3A] focus:outline-none transition-colors"
                  data-testid="lead-name-input"
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-2">Work Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="john@company.com"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-[#C41E3A] focus:outline-none transition-colors"
                  data-testid="lead-email-input"
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-2">Company Name *</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  placeholder="Your Company Ltd"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-[#C41E3A] focus:outline-none transition-colors"
                  data-testid="lead-company-input"
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-2">What story do you want to tell? (Optional)</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Tell us about your leadership visibility goals..."
                  rows={3}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-[#C41E3A] focus:outline-none transition-colors resize-none"
                  data-testid="lead-message-input"
                />
              </div>

              {/* Marketing Consent Checkbox - GDPR/DPDP Compliance */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="marketing_consent"
                  checked={formData.marketing_consent}
                  onChange={(e) => setFormData({...formData, marketing_consent: e.target.checked})}
                  className="mt-1 w-4 h-4 bg-zinc-800 border-zinc-600 rounded text-[#C41E3A] focus:ring-[#C41E3A]"
                  data-testid="lead-marketing-consent"
                />
                <label htmlFor="marketing_consent" className="text-zinc-400 text-sm leading-relaxed">
                  I agree to receive updates about leadership visibility solutions and promotional content from BrandStage.
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#C41E3A] hover:bg-[#A01830] disabled:bg-zinc-700 text-white py-4 px-6 rounded-lg font-bold transition-colors flex items-center justify-center gap-2"
                data-testid="lead-submit-btn"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Get Started
                  </>
                )}
              </button>
            </div>

            <p className="text-zinc-500 text-xs text-center mt-4">
              By submitting, you agree to our{' '}
              <Link to="/privacy" className="text-[#C41E3A] hover:underline">Privacy Policy</Link>
            </p>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-zinc-800">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png" 
              alt="BrandStage" 
              className="h-6"
            />
            <span className="text-zinc-500 text-sm">
              A Continex Company
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-zinc-500">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/" className="hover:text-white transition-colors">Main Site</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
